// avatarModelView.ts — rendu des modèles 3D (.glb) de l'onglet Ressources d'un créateur
//
// Deux usages :
//   - renderAvatarModelThumb : une image fixe du modèle, pour la tuile (un seul renderer
//     partagé, les vignettes sont fabriquées l'une après l'autre, puis gardées en mémoire) ;
//   - openAvatarModelViewer  : une vue vivante dans un canvas — le modèle tourne sur lui-même,
//     rejoue ses animations s'il en porte, et se manipule à la souris (orbite, zoom).
// Même chargeur que l'effet Avatar de la régie (meshopt + KTX2), pour lire les mêmes fichiers.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const KTX2_TRANSCODER_PATH = `https://cdn.jsdelivr.net/npm/three@0.${THREE.REVISION}.0/examples/jsm/libs/basis/`;

let ktx2: KTX2Loader | null = null;

function loader(renderer: THREE.WebGLRenderer): GLTFLoader {
  if (!ktx2) ktx2 = new KTX2Loader().setTranscoderPath(KTX2_TRANSCODER_PATH);
  ktx2.detectSupport(renderer);
  return new GLTFLoader().setKTX2Loader(ktx2).setMeshoptDecoder(MeshoptDecoder);
}

/** Lumière douce de studio : une clé, un contre-jour et un fond de ciel. */
function lights(scene: THREE.Scene): void {
  scene.add(new THREE.HemisphereLight(0xffffff, 0x334155, 1.4));
  const key = new THREE.DirectionalLight(0xffffff, 2.2); key.position.set(2, 3, 4); scene.add(key);
  const rim = new THREE.DirectionalLight(0x7aa7ff, 1.2); rim.position.set(-3, 2, -3); scene.add(rim);
}

/** Centre le modèle sur l'origine et le ramène à une hauteur utile de 2 unités, quelle que soit son échelle d'origine. */
function fit(model: THREE.Object3D): number {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const longest = Math.max(size.x, size.y, size.z) || 1;
  const scale = 2 / longest;
  model.scale.multiplyScalar(scale);
  const centered = new THREE.Box3().setFromObject(model);
  model.position.sub(centered.getCenter(new THREE.Vector3()));
  return scale;
}

function frame(camera: THREE.PerspectiveCamera, aspect: number): void {
  camera.aspect = aspect;
  camera.position.set(1.6, 0.9, 3.4);
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();
}

// ── Vignettes ────────────────────────────────────────────────────────────

const THUMB_SIZE = 384;
const thumbs = new Map<string, Promise<string>>();
let chain: Promise<unknown> = Promise.resolve();
let thumbRenderer: THREE.WebGLRenderer | null = null;

async function makeThumb(url: string): Promise<string> {
  if (!thumbRenderer) {
    thumbRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    thumbRenderer.setSize(THUMB_SIZE, THUMB_SIZE, false);
    thumbRenderer.setPixelRatio(1);
  }
  const renderer = thumbRenderer;
  const gltf = await loader(renderer).loadAsync(url);
  const scene = new THREE.Scene();
  lights(scene);
  scene.add(gltf.scene);
  fit(gltf.scene);
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 50);
  frame(camera, 1);
  renderer.setClearColor(0x000000, 0);
  renderer.render(scene, camera);
  const data = renderer.domElement.toDataURL('image/png');
  scene.traverse(o => {
    const m = o as THREE.Mesh;
    m.geometry?.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach(x => x.dispose());
  });
  return data;
}

/** Image du modèle (data URL), fabriquée une fois par url puis gardée. Rejette si le fichier est illisible. */
export function renderAvatarModelThumb(url: string): Promise<string> {
  let p = thumbs.get(url);
  if (!p) {
    // Un seul rendu à la fois : un contexte WebGL partagé, et la page reste fluide.
    p = chain.then(() => makeThumb(url));
    chain = p.catch(() => undefined);
    thumbs.set(url, p);
    p.catch(() => thumbs.delete(url));
  }
  return p;
}

// ── Visionneuse ──────────────────────────────────────────────────────────

export interface AvatarModelViewer {
  setSpinning(on: boolean): void;
  isSpinning(): boolean;
  dispose(): void;
}

/** Montre le modèle dans `canvas` : entrée en zoom, rotation lente, animations du fichier, orbite à la souris. */
export function openAvatarModelViewer(
  canvas: HTMLCanvasElement, url: string,
  onState: (s: { status: 'loading' | 'ready' | 'error'; animations: number }) => void,
): AvatarModelViewer {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  const scene = new THREE.Scene();
  lights(scene);
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 50);
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 2.2;
  controls.enablePan = false;
  controls.minDistance = 1.5;
  controls.maxDistance = 9;

  const resize = () => {
    const w = canvas.clientWidth || 1, h = canvas.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  frame(camera, 1);
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(canvas);

  let model: THREE.Object3D | null = null;
  let mixer: THREE.AnimationMixer | null = null;
  let disposed = false;
  let raf = 0;
  const clock = new THREE.Clock();
  let appear = 0; // 0 → 1 : entrée en zoom avec un léger dépassement

  onState({ status: 'loading', animations: 0 });
  loader(renderer).loadAsync(url).then(gltf => {
    if (disposed) return;
    model = gltf.scene;
    fit(model);
    scene.add(model);
    if (gltf.animations.length) {
      mixer = new THREE.AnimationMixer(model);
      gltf.animations.forEach(a => mixer!.clipAction(a).play());
    }
    onState({ status: 'ready', animations: gltf.animations.length });
  }).catch(e => {
    console.warn('[MODELES-3D] lecture impossible', e);
    if (!disposed) onState({ status: 'error', animations: 0 });
  });

  const tick = () => {
    raf = requestAnimationFrame(tick);
    const dt = clock.getDelta();
    mixer?.update(dt);
    if (model && appear < 1) {
      appear = Math.min(1, appear + dt / 0.7);
      const t = appear - 1;
      const back = 1 + 2.7 * t * t * t + 1.7 * t * t; // easeOutBack
      model.visible = true;
      scene.scale.setScalar(Math.max(0.001, back));
    }
    controls.update();
    renderer.render(scene, camera);
  };
  tick();

  return {
    setSpinning(on) { controls.autoRotate = on; },
    isSpinning()    { return controls.autoRotate; },
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      scene.traverse(o => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
        const mat = m.material as THREE.Material | THREE.Material[] | undefined;
        (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach(x => x.dispose());
      });
      renderer.dispose();
    },
  };
}
